import { lazy, Suspense, useEffect } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/app-shell';
import { ModulePicker, NoModules } from '@/features/shared/module-picker';
import { MODULES, moduleForPath } from '@/lib/modules';
import { defaultModuleFor } from '@/lib/preferences';
import { SignInScreen } from '@/features/shared/auth/sign-in-screen';
import {
  cashierMainSearchSchema,
  cashierTransfersSearchSchema,
  daysOffSearchSchema,
  devicesSearchSchema,
  employeeDetailSearchSchema,
  employeesSearchSchema,
  logsSearchSchema,
  payrollSearchSchema,
  referenceSearchSchema,
  reportsSearchSchema,
  timesheetSearchSchema,
  workLogsSearchSchema,
} from '@/routes-search';
import {
  gastronomyControlsSearchSchema,
  gastronomyDaySearchSchema,
  gastronomyPeriodSearchSchema,
} from '@/features/gastronomy/search';
import { cateringSettlementSearchSchema } from '@/features/catering/search';
import { isoDate, referenceKind, type AppModule, type IsoDate, type YearMonth } from '@overseer/contracts';
import { currentYearMonth, shiftMonth } from '@/lib/time';
import { useAuth, useCapabilities, useModules, usePreferences } from '@/lib/auth/auth-context';
import { Card, CardBody, CardHeader } from '@/components/ui/card';

/**
 * Routes, defined in code rather than generated from the filesystem.
 *
 * Code-based routing keeps the type safety and drops the codegen step and its
 * generated file. With a route count this small the file-based convention buys
 * nothing, and one less build-time generator is one less thing that can be stale
 * when a test fails.
 */

/**
 * Every screen behind the sign-in gate is loaded on demand.
 *
 * Splitting per route follows how the app is actually used: nobody visits nine
 * screens on sign-in, and the two that matter (dashboard, timesheet) are the
 * ones a shared vendor chunk covers anyway.
 *
 * `SignInScreen` is deliberately *not* lazy. It is the first thing an
 * unauthenticated visitor needs, and putting a spinner in front of a login form
 * to save bytes on a screen you are about to render is the wrong trade.
 */
const DashboardScreen = lazy(() =>
  import('@/features/hr/dashboard/dashboard-screen').then((module) => ({
    default: module.DashboardScreen,
  })),
);
const ReportsScreen = lazy(() =>
  import('@/features/hr/reports/reports-screen').then((module) => ({
    default: module.ReportsScreen,
  })),
);
const TimesheetScreen = lazy(() =>
  import('@/features/hr/timesheet/timesheet-screen').then((module) => ({
    default: module.TimesheetScreen,
  })),
);
const PayrollScreen = lazy(() =>
  import('@/features/hr/payroll/payroll-screen').then((module) => ({
    default: module.PayrollScreen,
  })),
);
const ReferenceScreen = lazy(() =>
  import('@/features/hr/reference/reference-screen').then((module) => ({
    default: module.ReferenceScreen,
  })),
);
const EmployeeListScreen = lazy(() =>
  import('@/features/hr/employees/employee-list-screen').then((module) => ({
    default: module.EmployeeListScreen,
  })),
);
const EmployeeDetailScreen = lazy(() =>
  import('@/features/hr/employees/employee-detail-screen').then((module) => ({
    default: module.EmployeeDetailScreen,
  })),
);
const DaysOffScreen = lazy(() =>
  import('@/features/hr/days-off/days-off-screen').then((module) => ({
    default: module.DaysOffScreen,
  })),
);
const UsersScreen = lazy(() =>
  import('@/features/admin/users/users-screen').then((module) => ({
    default: module.UsersScreen,
  })),
);
const RolesScreen = lazy(() =>
  import('@/features/admin/roles/roles-screen').then((module) => ({
    default: module.RolesScreen,
  })),
);
const CompaniesScreen = lazy(() =>
  import('@/features/admin/companies/companies-screen').then((module) => ({
    default: module.CompaniesScreen,
  })),
);
const SourcesScreen = lazy(() =>
  import('@/features/admin/sources/sources-screen').then((module) => ({
    default: module.SourcesScreen,
  })),
);
const LogsScreen = lazy(() =>
  import('@/features/admin/logs/logs-screen').then((module) => ({
    default: module.LogsScreen,
  })),
);

const CardReadersScreen = lazy(() =>
  import('@/features/hr/card-readers/card-readers-screen').then((module) => ({
    default: module.CardReadersScreen,
  })),
);
const WorkLogsScreen = lazy(() =>
  import('@/features/hr/work-logs/work-logs-screen').then((module) => ({
    default: module.WorkLogsScreen,
  })),
);
const WeatherSettingsScreen = lazy(() =>
  import('@/features/shared/weather/weather-settings-screen').then((module) => ({
    default: module.WeatherSettingsScreen,
  })),
);
const HotelDayScreen = lazy(() =>
  import('@/features/hotel/hotel-day-screen').then((module) => ({
    default: module.HotelDayScreen,
  })),
);
const HotelPeriodScreen = lazy(() =>
  import('@/features/hotel/hotel-period-screen').then((module) => ({
    default: module.HotelPeriodScreen,
  })),
);
const HotelForwardScreen = lazy(() =>
  import('@/features/hotel/hotel-forward-screen').then((module) => ({
    default: module.HotelForwardScreen,
  })),
);
const HotelSettingsScreen = lazy(() =>
  import('@/features/hotel/hotel-settings-screen').then((module) => ({
    default: module.HotelSettingsScreen,
  })),
);
const GastronomyDayScreen = lazy(() =>
  import('@/features/gastronomy/gastronomy-day-screen').then((module) => ({
    default: module.GastronomyDayScreen,
  })),
);
const GastronomyPeriodScreen = lazy(() =>
  import('@/features/gastronomy/gastronomy-period-screen').then((module) => ({
    default: module.GastronomyPeriodScreen,
  })),
);
const GastronomyForwardScreen = lazy(() =>
  import('@/features/gastronomy/gastronomy-forward-screen').then((module) => ({
    default: module.GastronomyForwardScreen,
  })),
);
const GastronomyControlsScreen = lazy(() =>
  import('@/features/gastronomy/gastronomy-controls-screen').then((module) => ({
    default: module.GastronomyControlsScreen,
  })),
);
const GastronomySettingsScreen = lazy(() =>
  import('@/features/gastronomy/gastronomy-settings-screen').then((module) => ({
    default: module.GastronomySettingsScreen,
  })),
);
const CateringDayScreen = lazy(() =>
  import('@/features/catering/catering-day-screen').then((module) => ({
    default: module.CateringDayScreen,
  })),
);
const CateringPeriodScreen = lazy(() =>
  import('@/features/catering/catering-period-screen').then((module) => ({
    default: module.CateringPeriodScreen,
  })),
);
const CateringControlsScreen = lazy(() =>
  import('@/features/catering/catering-controls-screen').then((module) => ({
    default: module.CateringControlsScreen,
  })),
);
const CateringSettlementScreen = lazy(() =>
  import('@/features/catering/catering-settlement-screen').then((module) => ({
    default: module.CateringSettlementScreen,
  })),
);
const CateringSettingsScreen = lazy(() =>
  import('@/features/catering/catering-settings-screen').then((module) => ({
    default: module.CateringSettingsScreen,
  })),
);
const CashierLanding = lazy(() =>
  import('@/features/cashier/cashier-landing').then((module) => ({
    default: module.CashierLanding,
  })),
);
const CashierCloseScreen = lazy(() =>
  import('@/features/cashier/close-screen').then((module) => ({
    default: module.CloseScreen,
  })),
);
const CashierReportsScreen = lazy(() =>
  import('@/features/cashier/reports-screen').then((module) => ({
    default: module.ReportsScreen,
  })),
);
const CashierTransfersRedirect = lazy(() =>
  import('@/features/cashier/cashier-landing').then((module) => ({
    default: module.TransfersRedirect,
  })),
);
const CashierMainDeskScreen = lazy(() =>
  import('@/features/cashier/main-desk-screen').then((module) => ({
    default: module.MainDeskScreen,
  })),
);
const CashierOverviewScreen = lazy(() =>
  import('@/features/cashier/overview-screen').then((module) => ({
    default: module.OverviewScreen,
  })),
);
const CashierSettingsScreen = lazy(() =>
  import('@/features/cashier/settings-screen').then((module) => ({
    default: module.CashierSettingsScreen,
  })),
);
const AccountScreen = lazy(() =>
  import('@/features/shared/account/account-screen').then((module) => ({
    default: module.AccountScreen,
  })),
);

/**
 * The authentication gate.
 *
 * One place, wrapping every route, rather than a per-route `beforeLoad` guard:
 * a guard you have to remember to add is a guard that will be missing from the
 * route somebody adds in a hurry.
 */
const Root = () => {
  const { status, signOut } = useAuth();
  const { modules, loaded } = useModules();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (status === 'loading') {
    return <BootSplash />;
  }

  if (status === 'signed-out' || status === 'new-password-required') {
    return <SignInScreen />;
  }

  // `/v1/me` decides which shell to render, so nothing is drawn until it
  // answers. Guessing would mean flashing the wrong module's nav, or worse the
  // "no application" screen, for the second it is in flight — and `modules: []`
  // is indistinguishable from "not loaded yet" if you do not check.
  if (!loaded) {
    return <BootSplash />;
  }

  if (modules.length === 0) {
    return <NoModules onSignOut={() => void signOut()} />;
  }

  // Which module's chrome to draw, from the URL rather than from state, so a
  // deep link renders the right nav on the first paint.
  const current = moduleForPath(pathname);

  // ⚠️ **`/` is the ONLY path handled here, and the check is an equality.**
  //
  // Rendering the picker for anything `moduleForPath` does not recognise would
  // swallow every route outside a module — `/no-such-page` would show the
  // picker instead of the not-found page, and so would any screen added at the
  // root later. Whether a path is inside a module and whether it is the
  // landing path are different questions.
  //
  // The landing decision also lives here and only here. Putting a copy in the
  // index route's component made the two race: `Root` renders the picker
  // *instead of* `<Outlet/>`, so that component never mounted and never
  // redirected, and every user met the picker — including the single-module
  // ones it must never appear for.
  if (pathname === '/') {
    return <ModuleLanding modules={modules} />;
  }

  // A path outside every module — `/account`, or a URL that matches nothing and
  // renders the not-found page. It still gets the chrome, with an empty bar:
  // no nav item can be current, and borrowing another module's is a lie.
  const nav = current ? MODULES[current].nav : EMPTY_NAV;

  return (
    <AppShell nav={nav} modules={modules} current={current}>
      {/*
        The boundary for the lazy screens above. `common.loading` rather than a
        skeleton on purpose: the chunk arrives in a frame or two on a LAN, and a
        skeleton that flashes is worse than a word that does not.
      */}
      <Suspense fallback={<ScreenFallback />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
};

const ScreenFallback = () => {
  const { t } = useTranslation();

  return <p className="text-sm text-fg-muted">{t('common.loading')}</p>;
};

const BootSplash = () => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <p className="text-sm text-fg-muted">{t('common.loading')}</p>
    </div>
  );
};

const NotFound = () => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader title={t('common.notFound')} description={t('common.notFoundDescription')} />
      <CardBody>
        <Link to="/" className="text-sm text-accent underline">
          {t('common.backToDashboard')}
        </Link>
      </CardBody>
    </Card>
  );
};

/** No module's bar. Shared screens and the not-found page render with this. */
const EMPTY_NAV = { left: [], right: [] };

const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFound });

/**
 * `/` — where a sign-in lands, before anybody has chosen anything.
 *
 * Sends the user to their module. With one module that is the whole story and
 * nothing is ever shown here, which is the point: **a user with one application
 * must see no picker and no extra click.**
 *
 * The picker for multi-module users arrives with the switcher; until then this
 * takes the first module, which for an Admin is HR.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // Renders nothing: `Root` intercepts `/` and decides between the landing
  // redirect and the picker. The route exists so `/` is not a 404.
  component: () => null,
});

/**
 * Where a sign-in lands.
 *
 * Three outcomes, and the first is the one that matters:
 *
 *  - **one module** — straight in, no picker, no extra click. Every real user
 *    today.
 *  - **several, one remembered** — straight to the remembered one.
 *  - **several, none remembered** — the picker, because there is a genuine
 *    choice to make and guessing at it would be wrong half the time.
 *
 * The navigation happens in an effect rather than during render: navigating
 * while rendering is a state update inside another component's render pass.
 * `replace`, so the back button does not bounce off `/` and straight forward
 * again.
 *
 * The remembered module is the person's, from the server, then this browser's.
 * With a choice to make, the landing waits for the server's answer: a picker
 * shown and then replaced by a redirect is a flash, and on a new browser it is
 * the whole point of saving the choice on the server.
 */
const ModuleLanding = ({ modules }: { modules: AppModule[] }) => {
  const navigate = useNavigate();
  const preferences = usePreferences();
  const waiting = modules.length > 1 && preferences.isPending;

  const target =
    modules.length === 1 ? modules[0] : waiting ? null : defaultModuleFor(modules, preferences.data?.general);

  useEffect(() => {
    if (target === null) return;

    void navigate({ to: MODULES[target].to as never, replace: true });
  }, [target, navigate]);

  if (waiting) return <BootSplash />;

  return target === null ? <ModulePicker modules={modules} /> : <BootSplash />;
};

/*
 * Redirects from the old, unprefixed URLs.
 *
 * Every HR path lives at the root in bookmarks, emailed links and saved tabs
 * that predate the `/hr` prefix. These redirect rather than 404.
 *
 * ⚠️ **`search` is forwarded, and that is not optional.** A bookmarked
 * `/timesheet?year=2026&month=6` that lands on the current month looks like data
 * loss rather than a redirect, and the person who sees it has no way to tell the
 * difference. Same for `/employees/454`, whose param is carried below.
 *
 * Temporary: kept until the logs show nothing is asking for them.
 */
const legacy = (path: string, to: string) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: ({ search }) => {
      throw redirect({ to, search, params: {} as never });
    },
  });

const legacyRoutes = [
  legacy('/timesheet', '/hr/timesheet'),
  legacy('/payroll', '/hr/payroll'),
  legacy('/employees', '/hr/employees'),
  legacy('/days-off', '/hr/days-off'),
  legacy('/work-logs', '/hr/work-logs'),
  legacy('/card-readers', '/hr/card-readers'),
  legacy('/users', '/admin/users'),
  legacy('/hr/users', '/admin/users'),
  legacy('/admin/collectors', '/admin/sources'),
  legacy('/reports', '/hr/reports'),
];

/**
 * The two old-URL redirects that carry a parameter.
 *
 * Separate from the list above because the parameter has to survive the
 * redirect — dropping it would send somebody looking at one employee to the list
 * of all of them, which reads as the app losing their place.
 */
const legacyEmployeeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/$employeeId',
  beforeLoad: ({ params, search }) => {
    // Parsed to a number here, because the target route's `parseParams` does the
    // same and its type says so. A raw string would reach the screen as `NaN`
    // after the redirect — a 400 from the API rendered as "something went wrong",
    // which is the failure the target route's own comment was written to prevent.
    throw redirect({
      to: '/hr/employees/$employeeId',
      params: { employeeId: Number(params.employeeId) },
      search,
    });
  },
});

const legacyReferenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reference/$kind',
  beforeLoad: ({ params, search }) => {
    throw redirect({ to: '/hr/reference/$kind', params, search });
  },
});

const hrIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr',
  component: DashboardScreen,
});

/*
 * Each route is declared explicitly rather than through a `plannedRoute(path,
 * …)` helper.
 *
 * A helper taking `path: string` widens the literal, so `addChildren` infers a
 * tree with no known paths and every `<Link to="…">` in the app collapses to
 * `'.' | '/' | '..'` — typed routing silently stops working, and the error
 * surfaces in the component that uses a link rather than here. Repetition is
 * the price of the path types.
 */
/**
 * The employee cost reports, a nav item of their own.
 *
 * The year, the month and the range are URL state, so "August, the year so
 * far" is a link.
 */
const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/reports',
  validateSearch: reportsSearchSchema,
  component: function Reports() {
    return <ReportsScreen search={reportsRoute.useSearch()} />;
  },
});

const timesheetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/timesheet',
  // The month, the filters and the display mode are all URL state, validated
  // here so a hand-edited or stale link degrades to the current month rather
  // than to an error.
  validateSearch: timesheetSearchSchema,
  component: function Timesheet() {
    return <TimesheetScreen search={timesheetRoute.useSearch()} />;
  },
});

/**
 * Payroll, one month at a time.
 *
 * Behind `canManagePayroll` at the API — every route under `/v1/payroll` answers
 * 403 to anyone else — and the nav item is gated on the same flag. There is no
 * `beforeLoad` guard here on purpose: the route existing is not the boundary, the
 * server is, and a client-side guard on top would only decide *which* screen a
 * user who typed the URL sees.
 */
const payrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/payroll',
  validateSearch: payrollSearchSchema,
  component: function Payroll() {
    return <PayrollScreen search={payrollRoute.useSearch()} />;
  },
});

const employeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/employees',
  validateSearch: employeesSearchSchema,
  component: function Employees() {
    return <EmployeeListScreen search={employeesRoute.useSearch()} />;
  },
});

/**
 * One person.
 *
 * The id is parsed here rather than in the screen, so a URL like
 * `/employees/nonsense` fails at the boundary instead of reaching a query as `NaN`
 * — which the API would answer with a 400 and the screen would render as "something
 * went wrong".
 */
const employeeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/employees/$employeeId',
  parseParams: (params) => ({ employeeId: Number(params.employeeId) }),
  stringifyParams: (params) => ({ employeeId: String(params.employeeId) }),
  // The month the strip shows. See the schema for why it is URL state.
  validateSearch: employeeDetailSearchSchema,
  component: function EmployeeDetail() {
    const { employeeId } = employeeDetailRoute.useParams();
    const search = employeeDetailRoute.useSearch();

    return Number.isInteger(employeeId) ? (
      <EmployeeDetailScreen employeeId={employeeId} search={search} />
    ) : (
      <NotFound />
    );
  },
});

const daysOffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/days-off',
  validateSearch: daysOffSearchSchema,
  component: function DaysOff() {
    return <DaysOffScreen search={daysOffRoute.useSearch()} />;
  },
});

/**
 * The five reference tables, one route with the kind in the path.
 *
 * `parseParams` validates against the contract's enum, so `/reference/wombats`
 * renders the not-found page rather than firing a request the API will reject.
 */
/**
 * What is kept on an Administracja screen now: a link to its old place goes
 * there, for whoever may open it.
 */
const MovedToAdmin = ({ to }: { to: '/admin/companies' | '/admin/sources' }) => {
  const navigate = useNavigate();
  const { loaded, canManageSettings } = useCapabilities();

  useEffect(() => {
    if (canManageSettings) void navigate({ to, replace: true });
  }, [canManageSettings, navigate, to]);

  return !loaded || canManageSettings ? <BootSplash /> : <NotFound />;
};

const referenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/reference/$kind',
  validateSearch: referenceSearchSchema,
  component: function Reference() {
    const { kind } = referenceRoute.useParams();
    const search = referenceRoute.useSearch();
    const parsed = referenceKind.safeParse(kind);

    if (kind === 'companies') return <MovedToAdmin to="/admin/companies" />;

    return parsed.success ? <ReferenceScreen kind={parsed.data} search={search} /> : <NotFound />;
  },
});

const workLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/work-logs',
  validateSearch: workLogsSearchSchema,
  component: function WorkLogs() {
    return <WorkLogsScreen search={workLogsRoute.useSearch()} />;
  },
});

/** Hotel: today — room grid and morning metrics. */
const hotelIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hotel',
  component: HotelDayScreen,
});

/** Hotel: period results. */
const hotelResultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hotel/results',
  component: HotelPeriodScreen,
});

/** Hotel: forward book — OTB and pickup. */
const hotelForwardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hotel/forward',
  component: HotelForwardScreen,
});

/**
 * Hotel settings: long-term rent, long blocks, meal rates.
 *
 * Write-gated on the server; the nav item is hidden without the capability.
 */
const hotelSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hotel/settings',
  component: HotelSettingsScreen,
});

/** The hotel collector's token is its source's, on Administracja → Źródła danych. */
const hotelSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hotel/sources',
  component: function HotelSources() {
    return <MovedToAdmin to="/admin/sources" />;
  },
});

/**
 * Gastronomy: one day. The date is URL state, so a link to a day opens on it.
 */
const gastronomyIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gastronomy',
  validateSearch: gastronomyDaySearchSchema,
  component: function GastronomyDay() {
    return <GastronomyDayScreen search={gastronomyIndexRoute.useSearch()} />;
  },
});

/** Gastronomy: a period, all outlets or one. */
const gastronomyResultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gastronomy/results',
  validateSearch: gastronomyPeriodSearchSchema,
  component: function GastronomyResults() {
    return <GastronomyPeriodScreen search={gastronomyResultsRoute.useSearch()} />;
  },
});

/** Gastronomy: reservations, pickup and the event pipeline from the latest snapshot. */
const gastronomyForwardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gastronomy/forward',
  component: GastronomyForwardScreen,
});

/** Gastronomy: deletes, voids, discounts and internal settlement, per authoriser. */
const gastronomyControlsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gastronomy/controls',
  validateSearch: gastronomyControlsSearchSchema,
  component: function GastronomyControls() {
    return <GastronomyControlsScreen search={gastronomyControlsRoute.useSearch()} />;
  },
});

/**
 * Gastronomy Ustawienia — outlets, where points of sale and series count, menu
 * categories. Read-only without `canEditGastronomySettings`.
 */
const gastronomySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gastronomy/settings',
  component: GastronomySettingsScreen,
});

/** The gastronomy collector's token is its source's, on Administracja → Źródła danych. */
const gastronomySourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gastronomy/sources',
  component: function GastronomySources() {
    return <MovedToAdmin to="/admin/sources" />;
  },
});

/** Catering (Stołówki): one day, gastronomy's Dziś with a canteen site as the outlet. */
const cateringIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catering',
  validateSearch: gastronomyDaySearchSchema,
  component: function CateringDay() {
    return <CateringDayScreen search={cateringIndexRoute.useSearch()} />;
  },
});

/** Catering: a period, gastronomy's Wyniki for the canteens. */
const cateringResultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catering/results',
  validateSearch: gastronomyPeriodSearchSchema,
  component: function CateringResults() {
    return <CateringPeriodScreen search={cateringResultsRoute.useSearch()} />;
  },
});

/** Catering: Kontrola, gastronomy's for the canteens, with the bills closed with no payment. */
const cateringControlsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catering/controls',
  validateSearch: gastronomyControlsSearchSchema,
  component: function CateringControls() {
    return <CateringControlsScreen search={cateringControlsRoute.useSearch()} />;
  },
});

/**
 * Catering Rozliczenie: a site's month with employee cards — the card report,
 * the companies the employer bills, and their invoice figures.
 */
const cateringSettlementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catering/settlement',
  validateSearch: cateringSettlementSearchSchema,
  component: function CateringSettlement() {
    return <CateringSettlementScreen search={cateringSettlementRoute.useSearch()} />;
  },
});

/**
 * Catering Ustawienia — the sites it reads, and which reasons are the employee
 * card. Read-only without `canEditCateringSettings`.
 */
const cateringSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catering/settings',
  component: CateringSettingsScreen,
});

/**
 * Cashier: the module root, sent on to the screen the user's job starts on.
 */
const cashierIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier',
  component: CashierLanding,
});

/**
 * Cashier: Raport dnia. The desk and the day are optional path params;
 * without them the screen takes the first desk and its next day to close. A
 * param that does not parse is treated as absent rather than sent to the API.
 */
const cashierCloseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/close/{-$deskId}/{-$date}',
  component: function CashierClose() {
    const { deskId, date } = cashierCloseRoute.useParams();

    return <CashierCloseScreen deskId={positiveInt(deskId)} date={calendarDate(date)} />;
  },
});

/**
 * Cashier: Historia raportów — one desk's month, or with `all` the months of
 * every desk the user sees, merged: the desk manager accepts days here.
 */
const cashierReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/reports/{-$deskId}/{-$year}/{-$month}',
  component: function CashierReports() {
    const { deskId, year, month } = cashierReportsRoute.useParams();

    return (
      <CashierReportsScreen
        deskId={deskId === ALL_DESKS ? ALL_DESKS : positiveInt(deskId)}
        period={yearMonth(year, month)}
      />
    );
  },
});

/**
 * Cashier: Kasa główna — the main desk's book, its balance and the month's
 * close. `?drawer=` opens the cash to accept or the payroll's cash pay.
 */
const cashierMainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/main/{-$deskId}/{-$year}/{-$month}',
  validateSearch: cashierMainSearchSchema,
  component: function CashierMain() {
    const { deskId, year, month } = cashierMainRoute.useParams();
    const { drawer } = cashierMainRoute.useSearch();

    return <CashierMainDeskScreen deskId={positiveInt(deskId)} period={yearMonth(year, month)} drawer={drawer} />;
  },
});

/*
 * The old addresses of the screens the cash moves left, kept working from
 * bookmarks: the days to accept are in Historia raportów; the cash to accept
 * and the payroll's cash pay are drawers of Kasa główna.
 */
const cashierInboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/inbox',
  beforeLoad: () => {
    throw redirect({ to: '/cashier/reports/{-$deskId}/{-$year}/{-$month}', params: {} });
  },
});

/** Kasa główna's drawer for whoever keeps a main desk, else Historia raportów: which is the user's, the landing knows. */
const cashierTransfersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/transfers',
  validateSearch: cashierTransfersSearchSchema,
  component: function CashierTransfers() {
    const { desk } = cashierTransfersRoute.useSearch();

    return <CashierTransfersRedirect deskId={desk} />;
  },
});

/** A payroll month's payouts are on the book of the month after it, whose drawer opens on that payroll month. */
const cashierPayoutsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/payouts/{-$deskId}/{-$year}/{-$month}',
  beforeLoad: ({ params }) => {
    const payroll =
      params.year === undefined && params.month === undefined
        ? shiftMonth(currentYearMonth(), -1)
        : yearMonth(params.year, params.month);
    const book = shiftMonth(payroll, 1);
    const deskId = positiveInt(params.deskId);
    throw redirect({
      to: '/cashier/main/{-$deskId}/{-$year}/{-$month}',
      params: { deskId: deskId === undefined ? undefined : String(deskId), year: String(book.year), month: String(book.month) },
      search: { drawer: 'payouts' },
    });
  },
});

/** Cashier: Przegląd — every main desk's year. The year defaults to the current one. */
const cashierOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/overview/{-$year}',
  component: function CashierOverview() {
    const { year } = cashierOverviewRoute.useParams();

    return <CashierOverviewScreen year={yearMonth(year, '1').year} />;
  },
});

/**
 * Cashier Ustawienia. Read with `cashier.read` or `cashier.settings`; written
 * with `canEditCashierSettings`.
 */
const cashierSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashier/settings',
  component: CashierSettingsScreen,
});

/** The reports path's desk for "Wszystkie punkty". */
const ALL_DESKS = 'all';

const positiveInt = (value: string | undefined): number | undefined => {
  const parsed = Number(value);

  return value !== undefined && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const calendarDate = (value: string | undefined): IsoDate | undefined => {
  const parsed = isoDate.safeParse(value);

  return parsed.success ? parsed.data : undefined;
};

/** A year and a 1-based month from the path, or the current month when either does not parse. */
const yearMonth = (year: string | undefined, month: string | undefined): YearMonth => {
  const y = positiveInt(year);
  const m = positiveInt(month);

  return y !== undefined && y >= 2000 && y <= 2100 && m !== undefined && m <= 12 ? { year: y, month: m } : currentYearMonth();
};

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountScreen,
});

const cardReadersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hr/card-readers',
  validateSearch: devicesSearchSchema,
  component: function CardReaders() {
    return <CardReadersScreen search={cardReadersRoute.useSearch()} />;
  },
});

/**
 * Admin: the module root, sent on to the first screen the person may open —
 * the users with `admin.users`, else the companies with `admin.settings`, else
 * the logs with `admin.logs`.
 */
const AdminLanding = () => {
  const navigate = useNavigate();
  const { loaded, canManageUsers, canManageSettings, canReadLogs } = useCapabilities();
  const to = !loaded
    ? null
    : canManageUsers
      ? '/admin/users'
      : canManageSettings
        ? '/admin/companies'
        : canReadLogs
          ? '/admin/logs'
          : '/admin/users';

  useEffect(() => {
    if (to !== null) void navigate({ to, replace: true });
  }, [to, navigate]);

  return <BootSplash />;
};

const adminIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminLanding,
});

/** Admin: who may sign in and what they may do. `admin.users` — `canManageUsers`. */
const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  component: UsersScreen,
});

/** Admin: the roles, each a set of permissions. `admin.users` — `canManageUsers`. */
const adminRolesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/roles',
  component: RolesScreen,
});

/** Admin: the companies every module names. `admin.settings` — `canManageSettings`. */
const adminCompaniesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/companies',
  component: CompaniesScreen,
});

/** Admin: the data sources, each with its token and last push. `admin.settings` — `canManageSettings`. */
const adminSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/sources',
  component: SourcesScreen,
});

/** Admin: where the weather beside the gastronomy figures is measured. `admin.settings` — `canManageSettings`. */
const adminWeatherRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/weather',
  component: WeatherSettingsScreen,
});

/** Admin: what the API logged, filtered by the URL. `admin.logs` — `canReadLogs`. */
const adminLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/logs',
  validateSearch: logsSearchSchema,
  component: function AdminLogs() {
    return <LogsScreen search={adminLogsRoute.useSearch()} />;
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  hrIndexRoute,
  reportsRoute,
  timesheetRoute,
  payrollRoute,
  employeesRoute,
  employeeDetailRoute,
  daysOffRoute,
  referenceRoute,
  workLogsRoute,
  cardReadersRoute,
  adminIndexRoute,
  adminUsersRoute,
  adminRolesRoute,
  adminCompaniesRoute,
  adminSourcesRoute,
  adminWeatherRoute,
  adminLogsRoute,
  accountRoute,
  hotelIndexRoute,
  hotelResultsRoute,
  hotelForwardRoute,
  hotelSettingsRoute,
  hotelSourcesRoute,
  gastronomyIndexRoute,
  gastronomyResultsRoute,
  gastronomyForwardRoute,
  gastronomyControlsRoute,
  gastronomySettingsRoute,
  gastronomySourcesRoute,
  cateringIndexRoute,
  cateringResultsRoute,
  cateringControlsRoute,
  cateringSettlementRoute,
  cateringSettingsRoute,
  cashierIndexRoute,
  cashierCloseRoute,
  cashierInboxRoute,
  cashierReportsRoute,
  cashierTransfersRoute,
  cashierMainRoute,
  cashierPayoutsRoute,
  cashierOverviewRoute,
  cashierSettingsRoute,
  ...legacyRoutes,
  legacyEmployeeDetailRoute,
  legacyReferenceRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
